import { readUIMessageStream, type UIMessage, type UIMessageChunk } from "ai";
import type { ExecutionJob } from "../types/executionJobTypes";
import { uiMessageToResultMessages } from "./convertResultMessagesToUIMessages";

type JobResult = NonNullable<ExecutionJob["result"]>;

type ExecutionMessageMetadata = {
  usage?: JobResult["usage"];
  cost?: JobResult["cost"];
  execution_time?: JobResult["execution_time"];
  finish_reason?: JobResult["finish_reason"] | null;
};

function isTerminalChunk(chunk: UIMessageChunk): boolean {
  return chunk.type === "finish" || chunk.type === "error" || chunk.type === "abort";
}

function extractMetadata(chunk: UIMessageChunk): ExecutionMessageMetadata | undefined {
  if (chunk.type === "finish" || chunk.type === "start" || chunk.type === "message-metadata") {
    const meta = chunk.messageMetadata as ExecutionMessageMetadata | undefined;
    return meta;
  }
  return undefined;
}

export type UIStreamJobPatch = {
  status?: ExecutionJob["status"];
  resultPatch: Partial<JobResult>;
};

/**
 * Per-execution reducer that turns `execution:ui-chunk` envelopes into
 * progressive `result.messages` snapshots via AI SDK `readUIMessageStream`.
 */
export class ExecutionUIStreamReducer {
  private controllers = new Map<string, ReadableStreamDefaultController<UIMessageChunk>>();
  private started = new Set<string>();
  /** Executions that already received finish/error/abort — ignore further chunks. */
  private finished = new Set<string>();

  /**
   * Feed a UI message chunk for an execution. Invokes `onUpdate` whenever a
   * new messages snapshot (or status change) is available.
   */
  handleChunk(
    executionId: string,
    chunk: UIMessageChunk,
    onUpdate: (patch: UIStreamJobPatch) => void,
  ): void {
    // Worker may emit a duplicate error after the stream error part; never reopen.
    if (this.finished.has(executionId)) {
      return;
    }

    let controller = this.controllers.get(executionId);

    if (!controller) {
      let streamController: ReadableStreamDefaultController<UIMessageChunk> | undefined;
      const stream = new ReadableStream<UIMessageChunk>({
        start(ctrl) {
          streamController = ctrl;
        },
      });
      controller = streamController!;
      this.controllers.set(executionId, controller);

      void (async () => {
        try {
          for await (const message of readUIMessageStream({ stream })) {
            // Status comes only from terminal chunks below — never from yields.
            onUpdate({
              resultPatch: {
                messages: uiMessageToResultMessages(message as UIMessage),
              },
            });
          }
        } catch (error) {
          console.error(`UI stream reducer error for ${executionId}:`, error);
          if (!this.finished.has(executionId)) {
            this.finished.add(executionId);
            onUpdate({
              status: "failed",
              resultPatch: {
                error: {
                  message: error instanceof Error ? error.message : "Stream error",
                },
              },
            });
          }
        } finally {
          this.controllers.delete(executionId);
          this.started.delete(executionId);
        }
      })();
    }

    if (!this.started.has(executionId)) {
      this.started.add(executionId);
      // Don't flash in_progress when the first chunk is already terminal.
      if (!isTerminalChunk(chunk)) {
        onUpdate({ status: "in_progress", resultPatch: {} });
      }
    }

    const metadata = extractMetadata(chunk);
    if (metadata) {
      onUpdate({
        resultPatch: {
          ...(metadata.usage !== undefined ? { usage: metadata.usage ?? undefined } : {}),
          ...(metadata.cost !== undefined ? { cost: metadata.cost ?? undefined } : {}),
          ...(metadata.execution_time !== undefined
            ? { execution_time: metadata.execution_time }
            : {}),
          ...(metadata.finish_reason !== undefined
            ? { finish_reason: metadata.finish_reason ?? undefined }
            : {}),
        },
      });
    }

    if (chunk.type === "error") {
      onUpdate({
        status: "failed",
        resultPatch: {
          error: { message: chunk.errorText },
        },
      });
    }

    try {
      controller.enqueue(chunk);
    } catch {
      // stream already closed
      return;
    }

    if (isTerminalChunk(chunk)) {
      this.finished.add(executionId);
      if (chunk.type === "finish") {
        onUpdate({ status: "completed", resultPatch: {} });
      } else if (chunk.type === "abort") {
        onUpdate({ status: "canceled", resultPatch: {} });
      }
      try {
        controller.close();
      } catch {
        // already closed
      }
    }
  }

  /** Force-close a job's stream (e.g. on cancel event without abort chunk). */
  close(executionId: string, _status: ExecutionJob["status"] = "canceled"): void {
    this.finished.add(executionId);
    const controller = this.controllers.get(executionId);
    if (!controller) return;
    try {
      controller.enqueue({ type: "abort" });
      controller.close();
    } catch {
      // ignore
    }
    this.controllers.delete(executionId);
    this.started.delete(executionId);
  }
}
